import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  useTheme,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Chip,
  Button,
  Avatar,
} from '@mui/material';
import {
  Search,
  ExpandMore,
  Article,
  PlayCircle,
  School,
  QuestionAnswer,
  Api,
  Security,
  Speed,
  Support,
  Book,
  Code,
  IntegrationInstructions,
  BugReport,
  ContactSupport,
  ArrowForward,
  CheckCircle,
  AutoAwesome,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';


const HelpCenter: React.FC = () => {
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [guideContents, setGuideContents] = useState<Record<string, string>>({});
  const [loadingGuides, setLoadingGuides] = useState(false);

  const helpCategories = [
    {
      icon: <School />,
      title: 'Getting Started',
      description: 'Create your account and upload your first content',
      articles: 8,
    },
    {
      icon: <Article />,
      title: 'Content Management',
      description: 'Upload, organize, manage files, and track version history',
      articles: 13,
    },
    {
      icon: <IntegrationInstructions />,
      title: 'Organizations & Teams',
      description: 'Structure your workspace and collaborate',
      articles: 10,
    },
    {
      icon: <Security />,
      title: 'Security & Privacy',
      description: 'Keep your data safe with 2FA and permissions',
      articles: 6,
    },
    {
      icon: <Support />,
      title: 'Subscriptions & Billing',
      description: 'Manage your subscription and payment methods',
      articles: 9,
    },
    {
      icon: <AutoAwesome />,
      title: 'AI & Smart Search',
      description: 'AI enrichment, semantic search, and natural language discovery',
      articles: 8,
    },
    {
      icon: <Speed />,
      title: 'Sharing & Links',
      description: 'Share content securely with public links',
      articles: 7,
    },
  ];

  const faqs = [
    {
      category: 'Getting Started',
      questions: [
        {
          q: 'How do I create a Tier One Content account?',
          a: 'Visit the homepage and click "Sign Up". Choose email/password or OAuth (Google/Microsoft). Verify your email to complete registration. You\'ll then create an organization and team to start uploading content.',
        },
        {
          q: 'What\'s the difference between an organization and a team?',
          a: 'Organizations are top-level containers (like your company), and teams are sub-groups within organizations (like departments or projects). Content belongs to teams. You need to create both an organization and at least one team to start using Tier One Content.',
        },
        {
          q: 'How do I upload my first content?',
          a: 'Select a team from the sidebar dropdown, click "Upload Content", then drag and drop your files or click to browse. By default AI fills in the title, description, and tags for you — you can toggle any of those off above the drop zone if you\'d rather set them manually. After files appear in the list, you can change each file\'s status, add manual tags, or use the checkboxes to apply changes to many files at once, then click Save & finish.',
        },
        {
          q: 'How do I invite team members?',
          a: 'Go to your team page, click the "Members" tab, then "Invite Member". In the dialog, type email addresses one per line (or separated by commas), or upload a CSV/TXT file with one address per line. Select a role for all invitees, add an optional personal message, and click "Send Invitations". You can invite one person or dozens at once — and add unlimited team members at no extra cost!',
        },
      ],
    },
    {
      category: 'Content & Search',
      questions: [
        {
          q: 'What file types can I upload?',
          a: 'Tier One Content supports all common file types: PDFs, Office documents (Word, Excel, PowerPoint), images (JPG, PNG, GIF), videos (MP4, MOV), audio files (MP3, WAV), code files, text files, archives (ZIP), and more. Maximum file size varies by plan (500MB to 10GB).',
        },
        {
          q: 'How do I organize my content?',
          a: 'Use teams to separate projects or departments, and tags for multi-dimensional organization. You can also create Views (custom pages) to showcase curated collections of content. Search and filter make it easy to find exactly what you need.',
        },
        {
          q: 'Can I search using plain English instead of keywords?',
          a: 'Yes! Tier One Content uses hybrid search that combines traditional keyword matching with AI-powered semantic search. You can type natural language queries like "training videos for conference room setup" or "content about selling to school districts" and the system finds relevant results even if your exact words don\'t appear in any file name or title.',
        },
        {
          q: 'Can I view files without downloading them?',
          a: 'Yes! Tier One Content includes a universal file viewer that supports PDFs, Office documents, images, videos, code files, and more. View files directly in your browser without downloading.',
        },
        {
          q: 'How do I share content with people outside my team?',
          a: 'Create a public link from the content details page. You can add password protection, set expiration dates, limit views, and control whether recipients can download. Perfect for sharing with clients or external partners.',
        },
      ],
    },
    {
      category: 'AI Enrichment',
      questions: [
        {
          q: 'What is AI enrichment?',
          a: 'When you upload a file, Tier One Content automatically analyzes its contents in the background and generates a plain-language description, a structured summary (key topics, intended audience, notable entities), and a suggested clean title. For audio and video, it also produces a transcript. This enriched metadata powers semantic search and makes your content easier to discover without any manual effort.',
        },
        {
          q: 'How long does AI enrichment take?',
          a: 'Enrichment runs asynchronously after upload — your file is immediately accessible in the library, and enrichment completes in the background (typically within a minute for most documents). A small processing indicator appears on the content tile while enrichment is in progress and disappears automatically when complete. Bulk uploads are enriched in parallel so you never wait for one file before the next is accepted.',
        },
        {
          q: 'Will the AI rename my file or change its title?',
          a: 'Only if you opt in. At upload time you can enable "Use AI-suggested title," which replaces the filename-derived title with a cleaner AI-generated one (e.g., turning "Final_v3_updated_NEW_2024.pptx" into "2024 Partner Sales Enablement Presentation") once enrichment completes. By default the AI\'s title suggestion is stored silently and offered as an option; your original title is never changed without your consent.',
        },
        {
          q: 'How does automatic tag classification work?',
          a: 'After enrichment, the AI compares the content against your organization\'s tag taxonomy and applies tags that meet a confidence threshold (80% by default). Suggestions that fall below the threshold are stored privately for admin review — they are never added to the public tag display automatically. The AI only applies tags that already exist in your taxonomy; it does not create new tags on its own. Any tags you apply manually during upload are merged with the AI\'s suggestions rather than replaced, so both coexist on the saved file.',
        },
        {
          q: 'Does AI enrichment work for images, audio, and video?',
          a: 'Yes. Images and slide decks receive visual descriptions and detected-text extraction. Audio and video with speech get a timestamped transcript that feeds the description and summary. Audio without speech (music, sound effects) receives descriptive metadata like mood, genre, and suggested use case. All of this content is then findable through natural language search.',
        },
        {
          q: 'Can I search inside a video or PDF by its contents?',
          a: 'Yes. For PDFs and documents, each page or section is indexed individually so a search for a topic mentioned only on page 14 of a 50-page document will surface that document with a relevant snippet. For videos and audio, transcript segments are indexed with timestamps so results can point you directly to the right moment in the recording.',
        },
      ],
    },
    {
      category: 'Version Management',
      questions: [
        {
          q: 'What is version management?',
          a: 'Version management lets you upload updated files for existing content while keeping a complete history. Each upload becomes a numbered version (v1, v2, v3…). Readers always see the current active version; publishers can browse history, activate older versions, and delete outdated ones.',
        },
        {
          q: 'What\'s the difference between "Add New Version" and "Replace Current"?',
          a: '"Add New Version" uploads the file as a draft that is invisible to readers until you explicitly activate it — perfect for staged rollouts. "Replace Current" immediately swaps the live file, inherits the current version\'s status, and permanently deletes the replaced file. Use Replace to correct a mistake without changing access level.',
        },
        {
          q: 'Can I upload a different file type as a new version?',
          a: 'No. All versions of a content item must be the same file type. If the original is a PDF, every new version must also be a PDF. The file picker restricts selection automatically, and the system rejects mismatched uploads with a clear error message.',
        },
        {
          q: 'How do I make a specific version the one readers see?',
          a: 'Open the Version History Panel (click the version chip next to the content title), find the version you want, and select "Make Active" from its action menu. The switch is immediate — readers see the new version right away.',
        },
        {
          q: 'Who can upload and manage versions?',
          a: 'The content:publish permission is required — TeamAdmin, ContentManager, and ContentPublisher roles all qualify. ContentReaders and Guests can see the current version only and are not shown the version history.',
        },
      ],
    },
    {
      category: 'Subscriptions & Billing',
      questions: [
        {
          q: 'How do concurrent user limits work?',
          a: 'Concurrent users are people actively using Tier One Content at the same time. You can have unlimited total team members, but only your plan\'s concurrent limit can be logged in simultaneously. For example, 10 concurrent seats typically serve 100+ total users, saving you 80-90% vs. traditional per-user pricing.',
        },
        {
          q: 'What plans are available?',
          a: 'Free (1 concurrent user, 5 teams, 2GB storage - forever free), Small (10 users, 10 teams, 200GB), Medium (25 users, 500GB), and Large (50 users, 1TB). All features are included in every plan. See our pricing page for current prices.',
        },
        {
          q: 'Can I change my plan anytime?',
          a: 'Yes! Upgrade or downgrade anytime from your subscription settings. Upgrades take effect immediately and are prorated. Downgrades take effect at your next billing cycle.',
        },
        {
          q: 'Are there discounts for annual billing?',
          a: 'Yes! Annual billing includes a discount compared to monthly billing. You can switch to annual billing anytime from your subscription settings. See our pricing page for details.',
        },
      ],
    },
    {
      category: 'Security & Permissions',
      questions: [
        {
          q: 'Is Tier One Content secure?',
          a: 'Yes! We use industry-standard encryption (TLS/HTTPS for data in transit, encrypted storage for data at rest), secure password hashing (bcrypt), and regular security audits. We strongly recommend enabling two-factor authentication for additional security.',
        },
        {
          q: 'What roles are available?',
          a: 'Organization roles: Owner (full control) and Member (create teams). Team roles: Admin (full team control), Content Manager (content oversight), Content Publisher (create/publish), and Content Reader (view-only). You can also create custom roles with specific permissions.',
        },
        {
          q: 'Should I enable two-factor authentication?',
          a: 'Strongly recommended! 2FA adds a second layer of security beyond your password. We support TOTP (authenticator apps like Google Authenticator or Authy) and email-based 2FA. Set it up in your profile settings under Security.',
        },
        {
          q: 'Can Tier One Content staff see my files?',
          a: 'No. Your content is private to your team. Our support staff cannot access your files without your explicit permission, which would only be granted for troubleshooting specific issues you report.',
        },
      ],
    },
    {
      category: 'Account Management',
      questions: [
        {
          q: 'I forgot my password. What do I do?',
          a: 'On the login page, click "Forgot Password?", enter your email, and you\'ll receive a password reset link. The link expires after 24 hours. If you signed up with OAuth (Google/Microsoft), you can also sign in that way.',
        },
        {
          q: 'How do I delete my account?',
          a: 'Go to Settings → Profile → Account Deletion. Note: You must leave all teams and cancel all subscriptions first. Account deletion is permanent after 30 days.',
        },
        {
          q: 'Can I belong to multiple organizations?',
          a: 'Yes! You can join unlimited organizations and teams. Use the Workspace Selector at the top of the sidebar — it shows all your organizations and their teams in one list. Expand an organization to see its teams, then click a team to select it.',
        },
      ],
    },
  ];

  const guides = [
    {
      id: 'getting-started',
      title: 'Getting Started Guide',
      description: 'Create your account, organization, and upload your first content',
      duration: '10 min read',
      level: 'Beginner',
      category: 'Getting Started',
      filename: '01-getting-started.md',
    },
    {
      id: 'authentication-security',
      title: 'Authentication & Security',
      description: 'Set up 2FA, manage OAuth providers, and secure your account',
      duration: '15 min read',
      level: 'All Levels',
      category: 'Security & Privacy',
      filename: '02-authentication-security.md',
    },
    {
      id: 'content-upload-management',
      title: 'Content Upload & Management',
      description: 'Master uploading, organizing, managing files, and version history',
      duration: '25 min read',
      level: 'Beginner',
      category: 'Content Management',
      filename: '03-content-upload-management.md',
    },
    {
      id: 'organizations-teams',
      title: 'Organizations & Teams',
      description: 'Structure your workspace and manage members effectively',
      duration: '15 min read',
      level: 'Admin',
      category: 'Organizations & Teams',
      filename: '04-organizations-teams.md',
    },
    {
      id: 'content-discovery-search',
      title: 'Content Discovery & Search',
      description: 'Find exactly what you need with filters and saved searches',
      duration: '15 min read',
      level: 'All Levels',
      category: 'Content Management',
      filename: '05-content-discovery-search.md',
    },
    {
      id: 'content-sharing-public-links',
      title: 'Content Sharing & Public Links',
      description: 'Share content securely with password protection and expiration',
      duration: '15 min read',
      level: 'Intermediate',
      category: 'Sharing & Links',
      filename: '06-content-sharing-public-links.md',
    },
    {
      id: 'views-page-builder',
      title: 'Views & Page Builder',
      description: 'Create custom pages with the drag-and-drop page builder',
      duration: '25 min read',
      level: 'Advanced',
      category: 'Content Management',
      filename: '07-views-page-builder.md',
    },
    {
      id: 'subscriptions-billing',
      title: 'Subscriptions & Billing',
      description: 'Understand concurrent user pricing and manage subscriptions',
      duration: '20 min read',
      level: 'Admin',
      category: 'Subscriptions & Billing',
      filename: '08-subscriptions-billing.md',
    },
    {
      id: 'roles-permissions-rbac',
      title: 'Roles & Permissions (RBAC)',
      description: 'Control access with roles and custom permissions',
      duration: '20 min read',
      level: 'Admin',
      category: 'Security & Privacy',
      filename: '09-roles-permissions-rbac.md',
    },
    {
      id: 'profile-account-management',
      title: 'Profile & Account Management',
      description: 'Manage your profile, preferences, and account settings',
      duration: '10 min read',
      level: 'All Levels',
      category: 'Getting Started',
      filename: '10-profile-account-management.md',
    },
    {
      id: 'file-viewer',
      title: 'File Viewer',
      description: 'View PDFs, videos, images, and code files in your browser',
      duration: '10 min read',
      level: 'All Levels',
      category: 'Content Management',
      filename: '11-file-viewer.md',
    },
    {
      id: 'tag-management',
      title: 'Tag Management',
      description: 'Organize content with tags and tag groups',
      duration: '15 min read',
      level: 'Intermediate',
      category: 'Content Management',
      filename: '12-tag-management.md',
    },
    {
      id: 'invitations',
      title: 'Invitation Management',
      description: 'Send, receive, and manage team invitations',
      duration: '10 min read',
      level: 'All Levels',
      category: 'Organizations & Teams',
      filename: '13-invitations.md',
    },
    {
      id: 'ratings-reviews',
      title: 'Content Ratings & Reviews',
      description: 'Rate content and write reviews for quality feedback',
      duration: '10 min read',
      level: 'All Levels',
      category: 'Content Management',
      filename: '14-ratings-reviews.md',
    },
    {
      id: 'faq-troubleshooting',
      title: 'FAQ & Troubleshooting',
      description: 'Common questions and solutions for all features',
      duration: 'Reference',
      level: 'All Levels',
      category: 'Getting Started',
      filename: '15-faq-troubleshooting.md',
    },
    {
      id: 'ai-content-enrichment',
      title: 'AI Content Enrichment',
      description: 'Understand automatic descriptions, summaries, title suggestions, and tag classification',
      duration: '15 min read',
      level: 'All Levels',
      category: 'AI & Smart Search',
      filename: '16-ai-content-enrichment.md',
    },
    {
      id: 'semantic-search',
      title: 'Semantic & Natural Language Search',
      description: 'Search with plain English questions and conceptual queries, not just keywords',
      duration: '10 min read',
      level: 'All Levels',
      category: 'AI & Smart Search',
      filename: '17-semantic-search.md',
    },
  ];

  // Load guide contents when component mounts
  useEffect(() => {
    const loadGuideContents = async () => {
      setLoadingGuides(true);
      const contents: Record<string, string> = {};

      try {
        await Promise.all(
          guides.map(async (guide) => {
            try {
              const response = await fetch(`/docs/user-guides/${guide.filename}`);
              if (response.ok) {
                contents[guide.id] = await response.text();
              }
            } catch (error) {
              console.error(`Failed to load guide ${guide.id}:`, error);
            }
          })
        );
        setGuideContents(contents);
      } catch (error) {
        console.error('Failed to load guide contents:', error);
      } finally {
        setLoadingGuides(false);
      }
    };

    loadGuideContents();
  }, []);

  // Filter functions
  const filterGuides = () => {
    let filtered = guides;

    // Filter by category
    if (selectedCategory) {
      filtered = filtered.filter(guide => guide.category === selectedCategory);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(guide => {
        // Search in metadata
        const metadataMatch =
          guide.title.toLowerCase().includes(query) ||
          guide.description.toLowerCase().includes(query) ||
          guide.level.toLowerCase().includes(query);

        // Search in full guide content if loaded
        const contentMatch = guideContents[guide.id]
          ? guideContents[guide.id].toLowerCase().includes(query)
          : false;

        return metadataMatch || contentMatch;
      });
    }

    return filtered;
  };

  const filterFaqs = () => {
    if (!searchQuery.trim()) {
      return faqs;
    }

    const query = searchQuery.toLowerCase();
    return faqs
      .map(category => ({
        ...category,
        questions: category.questions.filter(
          faq =>
            faq.q.toLowerCase().includes(query) ||
            faq.a.toLowerCase().includes(query)
        ),
      }))
      .filter(category => category.questions.length > 0);
  };

  const filteredGuides = filterGuides();
  const filteredFaqs = filterFaqs();

  const handleCategoryClick = (categoryTitle: string) => {
    setSelectedCategory(selectedCategory === categoryTitle ? null : categoryTitle);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategory(null);
  };


  return (
    <>
      {/* Compact Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.secondary.main}15 100%)`,
          pt: { xs: 4, md: 6 },
          pb: { xs: 3, md: 4 },
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              mb: 3,
              textAlign: 'center',
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Help Center
          </Typography>
          <Box sx={{ maxWidth: 800, mx: 'auto' }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search guides and FAQs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
                sx: {
                  backgroundColor: 'background.paper',
                  borderRadius: 2,
                },
              }}
            />
          </Box>
        </Container>
      </Box>

      {/* Unified Results Section */}
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Compact Filters */}
        <Box sx={{ mb: 3, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Filter:
          </Typography>
          {helpCategories.map((category) => (
            <Chip
              key={category.title}
              label={category.title}
              size="small"
              clickable
              onClick={() => handleCategoryClick(category.title)}
              color={selectedCategory === category.title ? 'primary' : 'default'}
              variant={selectedCategory === category.title ? 'filled' : 'outlined'}
            />
          ))}
          {(searchQuery || selectedCategory) && (
            <Button size="small" onClick={clearFilters} sx={{ ml: 'auto' }}>
              Clear All
            </Button>
          )}
        </Box>

        {/* Unified Results */}
        {(filteredGuides.length === 0 && filteredFaqs.length === 0) ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No results found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try a different search term or clear filters
            </Typography>
            <Button variant="outlined" onClick={clearFilters} sx={{ mt: 2 }}>
              Clear Filters
            </Button>
          </Box>
        ) : (
          <>
            {/* User Guides */}
            {filteredGuides.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h5" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Book fontSize="small" /> User Guides ({filteredGuides.length})
                </Typography>
                <Grid container spacing={2}>
                  {filteredGuides.map((guide, index) => (
                    <Grid item xs={12} md={6} key={index}>
                      <Paper
                        component={Link}
                        to={`/help/guide/${guide.id}`}
                        sx={{
                          p: 2,
                          cursor: 'pointer',
                          textDecoration: 'none',
                          display: 'block',
                          transition: 'all 0.2s',
                          '&:hover': {
                            boxShadow: 2,
                            transform: 'translateX(4px)',
                          },
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Chip label={guide.level} size="small" color="primary" variant="outlined" />
                          <Typography variant="caption" color="text.secondary">
                            {guide.duration}
                          </Typography>
                        </Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {guide.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {guide.description}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* FAQs */}
            {filteredFaqs.length > 0 && (
              <Box>
                <Typography variant="h5" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <QuestionAnswer fontSize="small" /> FAQs
                </Typography>
                {filteredFaqs.map((category) => (
                  <Box key={category.category} sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: 600 }}>
                      {category.category}
                    </Typography>
                    {category.questions.map((faq, index) => (
                      <Accordion
                        key={index}
                        elevation={0}
                        sx={{
                          mb: 1,
                          border: `1px solid ${theme.palette.divider}`,
                          '&:before': { display: 'none' },
                        }}
                      >
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {faq.q}
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Typography variant="body2" color="text.secondary">{faq.a}</Typography>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </Box>
                ))}
              </Box>
            )}
          </>
        )}
      </Container>

      {/* Contact Support Section */}
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <ContactSupport sx={{ fontSize: 64, color: 'primary.main', mb: 3 }} />
        <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
          Still Need Help?
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Our support team is here to help you succeed
        </Typography>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12} sm={6} md={4}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Email Support
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Get help via email within 24 hours (business days)
              </Typography>
              <Button variant="contained" fullWidth href="mailto:support@tieronecontent.com">
                Send Email
              </Button>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default HelpCenter;